import React, { useState, useEffect } from "react";
import { Button, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import LoaderInner from "../../components/LoaderInner";
import PricingPreview from "./pricingPreview";
import PlanItem from "./planItem";
import { getCookie } from "../../jwt/_helpers/cookie";
import axios from "axios";
import "./slaPlans.css";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const SLAPlans = ({currentSLAName, hasSLAPlan}) => {
  const token = getCookie("token");
  const [plans, setPlans] = useState([]);
  const [currentSLAOrder, setCurrentSLAOrder] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [planToUpgrade, setPlanToUpgrade] = useState(null);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const subscribeSLA = (plan) => {
    const subscriptionData = {
      price_id: plan.price_id,
    };

    setInProgress(true);

    const subscriptionOptions = {
      method: "POST",
      data: subscriptionData,
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    };

    axios(`${API_URL}wl-sla-plans/create-sla-subscription-checkout-session/`, subscriptionOptions)
    .then(res => {
      setInProgress(false);
      let checkoutUrl = res.data;
      window.location.assign(checkoutUrl);
    })
    .catch(err => {
      setInProgress(false);
      setError("Something went wrong, please try again later!");
    });
  };

  const upgradeSLA = (plan) => {
    const subscriptionData = {
      price_id: plan.price_id,
    };

    setInProgress(true);

    const subscriptionOptions = {
      method: "POST",
      data: subscriptionData,
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    };

    axios(`${API_URL}wl-sla-plans/upgrade-sla-subscription/`, subscriptionOptions)
    .then(res => {
      setInProgress(false);
      toggleModal();
      window.location.href = "/sla/payment/success/";
    })
    .catch(err => {
      setInProgress(false);
      toggleModal();
      window.location.href = "/sla/payment/failure/";
      setError("Something went wrong, please try again later!");
    });
  };

  useEffect(() => {
    const fetchOptions = {
      headers: {
        "Authorization": `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json",
      }
    };

    fetch(`${API_URL}wl-sla-plans/`, fetchOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        let slaPlans = data.results;
        slaPlans = slaPlans.sort((a, b) => a.order - b.order);
        setPlans(slaPlans);
        setLoading(false);
      })
      .catch(error => {
        console.error('An error occured while fetching wl sla plans:', error);
        setError('Failed to load wl sla plans');
        setLoading(false);
      });

  }, [token]);

  useEffect(() => {
    if (plans.length > 0 && currentSLAName !== "" && hasSLAPlan) {
      let currentSLAOrder = plans.find(plan => plan.name === currentSLAName);
      if (currentSLAOrder) {
        setCurrentSLAOrder(currentSLAOrder.order);
      }
    }
  }, [currentSLAName, plans]);

  if (loading) return <LoaderInner show={loading} />;
  if (error) return <Alert color="danger">Something went wrong, please try again later!</Alert>;

  return (
    <div style={{padding: '20px 40px'}}>
      {inProgress && <LoaderInner show={inProgress} />}
      <div className="row pricing-cards">
        {plans.map((plan) => (
          <div key={plan.id} className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
            <div className="subscription-card w-100" style={{border: (hasSLAPlan && currentSLAName === plan.name)? `4px solid ${plan.primary_color}`: `1px solid #6c757d`}}>
            {
              plan.ribbon_text && (
                <div className="subscription-card-ribbon-container">
                    <div className="subscription-card-ribbon" style={{background: plan.ribbon_color, color: plan.ribbon_text_color}}>{plan.ribbon_text}</div>
                </div>
              )
            }
            <center>
              <div className="plan-description-container">
                <h5>{plan.name} SLA - White Label - Rest API</h5>
              </div>
              <p className="plan-price-container">
                <div className="mb-3">
                  <span className="price" style={{color: plan.primary_color}}>{plan.price}</span>
                  <h6>{plan.price_info_message}</h6>
                </div>
                {(plan.price_type === "metered" && plan.price_per_unit) && (
                  <div>
                    Billed monthly based on usage
                    <br/>
                    <span className="ml-1 tooltip-icon mdi mdi-18px mdi-eye" style={{textDecoration: "underline"}} id={`price-ti-${plan.id}`}>&nbsp;Pricing preview</span>
                    <PricingPreview target={`price-ti-${plan.id}`} pricePerUnit={plan.price_per_unit} />
                  </div>
                )}
              </p>
            </center>
            <ul>
              {(plan?.items || []).map((planItem) => (
                <li key={`${plan.id}-${planItem.order}`} className="plan-item">
                  <strong className="plan-item-text">
                    {planItem.name}
                    {planItem.info_message.length > 0 && (
                      <>
                        <i className="ml-1 tooltip-icon mdi mdi-18px mdi-help-circle" style={{ verticalAlign: "middle"}} id={`ti-${plan.id}-${planItem.order}`}></i>
                        <PlanItem target={`ti-${plan.id}-${planItem.order}`} message={planItem.info_message} />
                      </>
                    )}
                  </strong>
                  {planItem.enabled ?
                    <i className="fas fa-check-circle plan-item-icon" style={{ color: plan.primary_color }}></i> :
                    <i className="fas fa-times-circle plan-item-icon" style={{ color: plan.secondary_color }}></i>
                  }
                </li>
              ))}
            </ul>
            {
              (hasSLAPlan && currentSLAName === plan.name)? 
              <Button
                className="btn btn-ml"
                style={{
                  backgroundColor: plan.primary_color,
                  '--hover-bg-color': plan.primary_color,
                }}
              >
                <b>Current Plan</b>
              </Button>:
              <Button
                className="btn btn-ml"
                style={{
                  backgroundColor: currentSLAOrder && currentSLAOrder>=plan.order? "#6c757d": plan.primary_color,
                  '--hover-bg-color': currentSLAOrder && currentSLAOrder>=plan.order? "#6c757d": plan.primary_color,
                }}
                disabled={currentSLAOrder && currentSLAOrder>=plan.order}
                onClick={() => {
                  if (hasSLAPlan) {
                    setPlanToUpgrade(plan);
                    toggleModal();
                  } else {
                    subscribeSLA(plan);
                  }
                }}
              >
                {hasSLAPlan? (currentSLAOrder && currentSLAOrder>=plan.order? "Subscribe": "Upgrade"): "Subscribe"}
              </Button>
            }
          </div>
          </div>
        ))}
      </div>
      <Modal isOpen={isModalOpen} toggle={toggleModal}>
        <LoaderInner show={inProgress} />
        <ModalHeader toggle={toggleModal}>Upgrade SLA</ModalHeader>
        <ModalBody>
          Are you sure you want to upgrade your SLA to <strong>{planToUpgrade? planToUpgrade.name: "new plan"}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-secondary float-right" disabled={inProgress} onClick={() => {toggleModal()}}>Cancel</Button>{' '}
          <Button className="btn btn-success float-left" disabled={inProgress} onClick={() => {upgradeSLA(planToUpgrade)}}>Upgrade</Button>{' '}
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default SLAPlans;